import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Phone, Mail, Clock, Shield, Star, Award, ChevronRight, MapPin, FileText, Wrench } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSiteContent } from '@/context/SiteContentContext';
import { pmsFrom } from '@/integrations/pms';
import DynamicIcon from './DynamicIcon';
import { PromoBanner } from './PromoBanner';

interface SidebarLink {
  title: string;
  href: string;
  icon?: string;
}

interface PageSidebarProps {
  /** Show the main CTA card */
  showCta?: boolean;
  /** Show trust badges */
  showTrustBadges?: boolean;
  /** Show promo banner */
  showPromo?: boolean;
  /** Related links to show (overrides auto-generated) */
  relatedLinks?: SidebarLink[];
  /** Section title for related links */
  relatedTitle?: string;
  /** Show service areas preview */
  showServiceAreas?: boolean;
  /** Show recent blog posts */
  showRecentPosts?: boolean;
  /** Show sibling services (auto-detected from context) */
  showRelatedServices?: boolean;
  /** Current entity context for smart linking */
  entityContext?: {
    type: 'service' | 'category' | 'segment' | 'location' | 'blog' | 'page';
    id?: string;
    categoryId?: string;
    segmentId?: string;
    slug?: string;
  };
  /** Custom CTA title */
  ctaTitle?: string;
  /** Custom CTA description */
  ctaDescription?: string;
}

export const PageSidebar: React.FC<PageSidebarProps> = ({
  showCta = true,
  showTrustBadges = true,
  showPromo = true,
  relatedLinks,
  relatedTitle = 'Related Services',
  showServiceAreas = true,
  showRecentPosts = true,
  showRelatedServices = true,
  entityContext,
  ctaTitle = 'Ready to Get Started?',
  ctaDescription = 'Contact us today for a free estimate on your project.',
}) => {
  const { template, serviceAreas } = useSiteContent();
  const phone = template.contact?.phone || '';
  const hours = template.contact?.hours || 'Mon-Fri 8am-6pm';
  const trustBadges = template.trustBadges || [];

  // ─── Fetch sibling/related services for contextual linking ───
  const { data: relatedServicesData } = useQuery({
    queryKey: ['sidebar-related-services', entityContext?.categoryId, entityContext?.id],
    queryFn: async () => {
      if (!entityContext?.categoryId) return [];
      const { data } = await pmsFrom('services')
        .select('name, slug, icon, category_id')
        .eq('category_id', entityContext.categoryId)
        .eq('is_active', true)
        .neq('id', entityContext.id || '')
        .order('sort_order')
        .limit(8);
      return data || [];
    },
    enabled: showRelatedServices && !!entityContext?.categoryId,
    staleTime: 1000 * 60 * 5,
  });

  // ─── Fetch parent category + segment for breadcrumb context ───
  const { data: categoryData } = useQuery({
    queryKey: ['sidebar-category', entityContext?.categoryId],
    queryFn: async () => {
      if (!entityContext?.categoryId) return null;
      const { data } = await pmsFrom('service_categories')
        .select('name, slug, segment_id')
        .eq('id', entityContext.categoryId)
        .single();
      if (!data) return null;
      const { data: seg } = await pmsFrom('service_segments')
        .select('name, slug')
        .eq('id', data.segment_id)
        .single();
      return { category: data, segment: seg };
    },
    enabled: !!entityContext?.categoryId,
    staleTime: 1000 * 60 * 5,
  });

  // ─── Fetch service locations for geo-linking ───
  const { data: serviceLocations = [] } = useQuery({
    queryKey: ['sidebar-locations'],
    queryFn: async () => {
      const { data } = await pmsFrom('service_locations')
        .select('name, slug, region')
        .eq('is_published', true)
        .order('sort_order')
        .limit(12);
      return data || [];
    },
    enabled: showServiceAreas,
    staleTime: 1000 * 60 * 5,
  });

  // ─── Fetch recent blog posts for topical authority ───
  const { data: recentPosts = [] } = useQuery({
    queryKey: ['sidebar-recent-posts'],
    queryFn: async () => {
      const { data } = await pmsFrom('blog_posts')
        .select('title, slug, category, published_at')
        .eq('is_published', true)
        .order('published_at', { ascending: false })
        .limit(4);
      return data || [];
    },
    enabled: showRecentPosts,
    staleTime: 1000 * 60 * 5,
  });

  // Build contextual related links from sibling services
  const contextualLinks: SidebarLink[] = relatedLinks || [];
  if (!relatedLinks && relatedServicesData && relatedServicesData.length > 0 && categoryData) {
    const segSlug = categoryData.segment?.slug || '';
    const catSlug = categoryData.category?.slug || '';
    relatedServicesData.forEach(s => {
      contextualLinks.push({
        title: s.name,
        href: `/${segSlug}/${catSlug}/${s.slug}`,
        icon: s.icon || undefined,
      });
    });
  }

  return (
    <aside className="space-y-6" aria-label="Page sidebar" role="complementary">
      {/* ── 1. Primary CTA Card ── */}
      {showCta && (
        <div className="bg-foreground text-background p-6 rounded-xl" role="region" aria-label="Contact us">
          <h3 className="font-heading font-semibold text-lg mb-2">{ctaTitle}</h3>
          <p className="text-sm text-background/70 mb-4">
            {ctaDescription}
          </p>
          <div className="space-y-3">
            <Button asChild className="w-full bg-primary text-primary-foreground hover:bg-primary/90">
              <a
                href={`tel:${phone.replace(/\D/g, '')}`}
                className="flex items-center justify-center gap-2"
                aria-label={`Call us at ${phone}`}
              >
                <Phone className="h-4 w-4" />
                {phone || 'Call Now'}
              </a>
            </Button>
            <Button
              asChild
              variant="outline"
              className="w-full bg-transparent border-background/30 hover:bg-background/10 text-background"
            >
              <Link to="/contact">Request a Free Quote</Link>
            </Button>
          </div>

          {/* Quick contact info */}
          <div className="mt-4 pt-4 border-t border-background/20 space-y-2 text-sm">
            <div className="flex items-center gap-2 text-background/70">
              <Clock className="h-4 w-4" aria-hidden="true" />
              <span>{hours}</span>
            </div>
            {template.contact?.email && (
              <div className="flex items-center gap-2 text-background/70">
                <Mail className="h-4 w-4" aria-hidden="true" />
                <a href={`mailto:${template.contact.email}`} className="truncate hover:underline">
                  {template.contact.email}
                </a>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── 2. Trust Badges & Social Proof ── */}
      {showTrustBadges && trustBadges.length > 0 && (
        <nav className="bg-card p-5 rounded-xl border border-border" aria-label="Trust signals">
          <h4 className="font-semibold text-sm mb-4 text-muted-foreground uppercase tracking-wider">
            Why Choose Us
          </h4>
          <ul className="space-y-3" role="list">
            {trustBadges.map((badge, index) => (
              <li key={index} className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <DynamicIcon name={badge.icon} className="h-4 w-4 text-primary" />
                </div>
                <span className="text-sm font-medium">{badge.label}</span>
              </li>
            ))}
          </ul>

          {/* Stats - Social proof signals */}
          <div className="mt-4 pt-4 border-t border-border grid grid-cols-2 gap-3">
            <div className="text-center">
              <div className="text-xl font-bold text-primary">
                {template.stats?.googleRating || 4.9}
              </div>
              <div className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                <Star className="h-3 w-3 text-primary" aria-hidden="true" />
                Google Rating
              </div>
            </div>
            <div className="text-center">
              <div className="text-xl font-bold text-primary">
                {template.stats?.projectsCompleted || 500}+
              </div>
              <div className="text-xs text-muted-foreground">
                Projects Done
              </div>
            </div>
          </div>
        </nav>
      )}

      {/* ── 3. Contextual Related Services (sibling internal links) ── */}
      {contextualLinks.length > 0 && (
        <nav className="bg-card p-5 rounded-xl border border-border" aria-label="Related services">
          <div className="flex items-center gap-2 mb-3">
            <Wrench className="h-4 w-4 text-primary" aria-hidden="true" />
            <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider">
              {relatedTitle}
            </h4>
          </div>
          <ul className="space-y-1" role="list">
            {contextualLinks.map((link, index) => (
              <li key={index}>
                <Link
                  to={link.href}
                  className="flex items-center gap-2 p-2 -mx-2 rounded-lg hover:bg-muted/50 transition-colors group"
                >
                  {link.icon ? (
                    <DynamicIcon name={link.icon} className="h-4 w-4 text-primary" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" aria-hidden="true" />
                  )}
                  <span className="text-sm group-hover:text-primary transition-colors">{link.title}</span>
                </Link>
              </li>
            ))}
          </ul>
          {/* Link up to parent category for hub-and-spoke */}
          {categoryData && (
            <div className="mt-3 pt-3 border-t border-border">
              <Link
                to={`/${categoryData.segment?.slug}/${categoryData.category.slug}`}
                className="text-sm font-medium text-primary hover:underline inline-flex items-center gap-1"
              >
                All {categoryData.category.name} Services <ChevronRight className="h-3 w-3" />
              </Link>
            </div>
          )}
        </nav>
      )}

      {/* ── 4. Service Areas (Geo-targeting links) ── */}
      {showServiceAreas && serviceLocations.length > 0 && (
        <nav className="bg-muted p-5 rounded-xl" aria-label="Service areas">
          <div className="flex items-center gap-2 mb-3">
            <MapPin className="h-4 w-4 text-primary" aria-hidden="true" />
            <h4 className="font-semibold text-sm">Areas We Serve</h4>
          </div>
          <ul className="flex flex-wrap gap-1.5 mb-3" role="list">
            {serviceLocations.slice(0, 8).map((loc, index) => (
              <li key={index}>
                <Link
                  to={`/service-areas/${loc.slug}`}
                  className="px-2 py-0.5 bg-background rounded text-xs hover:bg-primary/10 hover:text-primary transition-colors inline-block"
                >
                  {loc.name}
                </Link>
              </li>
            ))}
            {serviceLocations.length > 8 && (
              <li>
                <Link
                  to="/service-areas"
                  className="px-2 py-0.5 bg-primary/10 text-primary rounded text-xs font-medium inline-block hover:bg-primary/20 transition-colors"
                >
                  +{serviceLocations.length - 8} more
                </Link>
              </li>
            )}
          </ul>
          <Link
            to="/service-areas"
            className="text-sm font-medium text-primary hover:underline inline-flex items-center gap-1"
          >
            View all service areas <ChevronRight className="h-3 w-3" />
          </Link>
        </nav>
      )}

      {/* ── 5. Promo Banner ── */}
      {showPromo && (
        <PromoBanner variant="card" />
      )}

      {/* ── 6. Recent Blog Posts (Topical Authority) ── */}
      {showRecentPosts && recentPosts.length > 0 && (
        <nav className="bg-card p-5 rounded-xl border border-border" aria-label="Recent articles">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="h-4 w-4 text-primary" aria-hidden="true" />
            <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider">
              Latest Articles
            </h4>
          </div>
          <ul className="space-y-3" role="list">
            {recentPosts.map((post, index) => (
              <li key={index}>
                <Link
                  to={`/blog/${post.slug}`}
                  className="group block"
                >
                  <span className="text-sm font-medium group-hover:text-primary transition-colors line-clamp-2">
                    {post.title}
                  </span>
                  {post.category && (
                    <span className="text-xs text-muted-foreground mt-0.5 block">{post.category}</span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
          <div className="mt-3 pt-3 border-t border-border">
            <Link
              to="/blog"
              className="text-sm font-medium text-primary hover:underline inline-flex items-center gap-1"
            >
              Read more articles <ChevronRight className="h-3 w-3" />
            </Link>
          </div>
        </nav>
      )}

      {/* ── 7. Financing note ── */}
      <div className="bg-muted/50 p-4 rounded-xl border border-border" role="region" aria-label="Financing options">
        <div className="flex items-start gap-3">
          <Award className="h-5 w-5 text-primary shrink-0 mt-0.5" aria-hidden="true" />
          <div>
            <h4 className="font-semibold text-sm mb-1">Financing Available</h4>
            <p className="text-xs text-muted-foreground">
              Flexible payment options to fit your budget. Ask about our financing plans.
            </p>
            <Link to="/contact" className="text-xs font-medium text-primary hover:underline mt-1 inline-block">
              Learn more →
            </Link>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default PageSidebar;